// Bootstrap-time API/ID endpoint resolution for the admin dashboard (DEV-1057).
//
// Runs in the thin `index.tsx` entry BEFORE the render module is dynamically
// imported, so `config/env.ts`'s `API_URL`/`ID_URL` consts freeze at the
// resolved value. On a Pages preview host it probes the per-PR API stand and, if
// alive, points both the API and the OAuth issuer at it (the stand is its own
// issuer — opaque tokens live in the stand DB, so a prod-issuer token would be
// rejected). Off-preview it is a no-op and `config/env.ts` uses env/prod.

import { deriveAdminApiStandUrl, setResolvedEndpoints } from './runtime-endpoints';

const PROD_API_URL = 'https://api.symoditi.com';
const PROD_ID_URL = 'https://id.symoditi.com';
const PROBE_TIMEOUT_MS = 2000;

async function standAlive(candidate: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${candidate}/health/ready`, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    return res.ok;
  } catch {
    // Network error, timeout (abort), CORS failure — treat as no stand.
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve and store the runtime API/ID endpoints. Never throws.
 *
 * - Off a preview host: no-op — the store stays null and `config/env.ts` falls
 *   back to the build-time `VITE_API_URL` (local dev) or the prod default.
 * - On a preview host: ALWAYS populates the store (stand if the health probe
 *   succeeds within ~2s, else prod), so a stale CF `VITE_TS_API_URL` can never win.
 */
export async function resolveAndStoreEndpoints(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): Promise<void> {
  const candidate = deriveAdminApiStandUrl(hostname);
  if (!candidate) return;
  const alive = await standAlive(candidate);
  setResolvedEndpoints(
    alive ? { apiUrl: candidate, idUrl: candidate } : { apiUrl: PROD_API_URL, idUrl: PROD_ID_URL }
  );
}
